<script lang="ts">
import { defineComponent, onMounted, Ref } from 'vue'
import { createNamespacedHelpers } from 'vuex-composition-helpers'
import { LOCAL_STORAGE_REDIRECT_URI_KEY } from '@/constants'
import { Tenant } from '@/types'
import { client } from '@/services/beef'
import { OmitFirstArg } from '@/store'
import { actions as userActions } from '@/store/modules/User'
import { isAllowedRedirectUri } from '@/services/trusted-device/is-allowed-redirect-uri'
import { SubmitPayload } from '../../components/types'
import Activate from '../../components/Activate.vue'
import { useRoute, router } from '../../router'

const { useActions: useUserStoreActions } = createNamespacedHelpers('User')
const { useState: useLoginDomainStoreState } = createNamespacedHelpers('LoginDomain')

type OrderPartner = 'littlehotelier' | 'siteminder'

/**
 * Create password from the order email link:
 * /order?email=&partner=&langCode=&redirectUri=. The email is trusted, so it is shown read-only,
 * and setting the password verifies it. No secret question.
 */
export default defineComponent({
  name: 'OrderPage',

  components: {
    Activate,
  },

  setup() {

    const currentRoute = useRoute()

    const authenticateAgainstAuth0 = useUserStoreActions(['authenticateAgainstAuth0']).authenticateAgainstAuth0 as OmitFirstArg<typeof userActions.authenticateAgainstAuth0>
    const tenant = useLoginDomainStoreState(['tenant']).tenant as Ref<Tenant>

    const query = currentRoute.value?.query ?? {}
    const email = query.email as string
    const partner = query.partner as OrderPartner
    const langCode = query.langCode as string | undefined
    const redirectUri = query.redirectUri as string

    const goToLogin = () => router.replace({ name: 'LoginPage', query: { email, redirectUri } })

    // Activate's token step: decides whether this email creates a password, signs in, or can't continue
    const checkOrderStatus = async () => {

      if (!email || !partner || !isAllowedRedirectUri(redirectUri)) {
        throw new Error('OrderLinkInvalid')
      }

      const { state } = await client.getOrderStatus({ body: { email, partner } })

      if (state === 'signin') {
        goToLogin()
      }

    }

    onMounted(() => {

      if (isAllowedRedirectUri(redirectUri)) {
        localStorage.setItem(LOCAL_STORAGE_REDIRECT_URI_KEY, redirectUri)
      }

    })

    const submit = async (payload: SubmitPayload) => {

      try {

        await client.registerOrderUser({
          body: {
            email,
            partner,
            password: payload.password,
            ...(langCode ? { langCode } : {}),
          },
        })

      } catch (error) {

        if (error.response?.status === 409 && error.response?.data?.code === 'UserAlreadyExists') {
          goToLogin()
          return
        }

        throw error

      }

      await authenticateAgainstAuth0({
        user: { email, password: payload.password },
        tenant: tenant.value,
      })

    }

    return {
      email,
      submit,
      checkOrderStatus,
    }

  },

})

</script>

<template>
  <Activate
    :submit="submit"
    :validate-token="checkOrderStatus"
    :requires-token="false"
    :email="email"
    hide-secret-question
  />
</template>
